/**
 * Order Delivery Date (Identixweb) — same-day  delivery time rules (FR-23)
 *
 *   1. If the selected delivery date is today, hide the "AM" option
 *      (same-day delivery only ever happens in the afternoon).
 *   2. If it's also already past the 1:00 PM cutoff, hide "PM" too
 *      (past cutoff, today no longer has ANY valid delivery time).
 *
 * Verified live against test store on 2026-08-26.
 * The app renders:
 *   - a calendar built with "react-multi-date-picker" (classes prefixed "rmdp-")
 *       -> today's cell always carries .rmdp-today
 *       -> the chosen cell always carries .rmdp-selected
 *       -> today AND selected at once => .rmdp-day.rmdp-today.rmdp-selected
 *   - a time dropdown built with Ant Design's Select (classes prefixed "ant-select-")
 *       -> the trigger:   .ant-select.identixweb-order-delivery-deliveryTime
 *       -> the open list: .ant-select-dropdown (portal'd elsewhere in the DOM,
 *          so we watch document.body rather than the widget itself)
 *       -> each row:      .ant-select-item-option, text content "AM" or "PM"
 *
 * How it works: a single MutationObserver watches the whole page. Any time
 * the DOM changes (the dropdown opening, or its "hidden" class toggling),
 * it re-scans every open dropdown and shows/hides AM/PM rows based on
 * today's date + the cutoff check below. A once-a-minute timer re-runs the
 * same check even while nothing else changes on the page, so a cart left
 * open across the 1:00 PM cutoff still gets corrected without a reload.
 *
 * IMPORTANT — timezone: the 1:00 PM cutoff is evaluated in the store's own
 * timezone (STORE_TIMEZONE below), NOT the visitor's device clock. Set this
 * to whatever timezone Shopify Admin > Settings > General shows for this
 * store; it defaults to Europe/Malta based on the Born team's account
 * timezone, but confirm it against the actual store setting before shipping.
 *
 * Where to add this: Shopify Admin > Online Store > Themes > Edit code >
 * layout/theme.liquid, pasted in a <script> tag just before </body>, so it
 * runs on every page the widget can appear on (cart, product, etc).
 *
 * Caveat: this reads the app's rendered markup, not a public API. If
 * Identixweb changes their calendar/select library or class names in a
 * future update, re-check this after updating the app. It also only hides
 * time options — it does not disable today's date in the calendar itself.
 * If Identixweb's own "Active Cut-off Time" setting (Shipping Settings) is
 * configured for 1:00 PM, today's date will already be removed from the
 * calendar after cutoff and this script's PM-hiding branch will rarely
 * trigger; it exists as a safety net in case that app setting is off,
 * misconfigured, or reverted.
 */
(function () {
  'use strict';

  // ---- configuration -------------------------------------------------
  var STORE_TIMEZONE = 'Europe/Malta'; // confirm against Shopify Admin > Settings > General
  var CUTOFF_HOUR = 13;                // 1:00 PM, 24-hour clock
  var CUTOFF_MINUTE = 0;
  var RECHECK_INTERVAL_MS = 60 * 1000; // re-run the check once a minute
  // ---------------------------------------------------------------------

  var TODAY_SELECTED_SELECTOR = '.rmdp-day.rmdp-today.rmdp-selected';
  var DROPDOWN_SELECTOR = '.ant-select-dropdown';
  var OPTION_SELECTOR = '.ant-select-item-option';

  function isTodaySelected() {
    return !!document.querySelector(TODAY_SELECTED_SELECTOR);
  }

  // Reads the current time in STORE_TIMEZONE (independent of the visitor's
  // own device timezone) and returns true once it's at/after the cutoff.
  function isPastCutoff() {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: STORE_TIMEZONE,
        hour12: false,
        hour: 'numeric',
        minute: 'numeric'
      }).formatToParts(new Date());

      var hour = Number(parts.find(function (p) { return p.type === 'hour'; }).value);
      var minute = Number(parts.find(function (p) { return p.type === 'minute'; }).value);

      // Intl can report hour 24 for midnight in some engines; normalize it.
      if (hour === 24) hour = 0;

      return (hour > CUTOFF_HOUR) || (hour === CUTOFF_HOUR && minute >= CUTOFF_MINUTE);
    } catch (e) {
      // If the timezone lookup ever fails, fail safe by NOT hiding PM —
      // an AM slot bug is what FR-23 is about, so don't introduce a new
      // one that blocks legitimate PM same-day orders.
      return false;
    }
  }

  function setOptionHidden(option, hidden) {
    option.style.display = hidden ? 'none' : '';
    if (hidden) {
      option.setAttribute('aria-hidden', 'true');
    } else {
      option.removeAttribute('aria-hidden');
    }
  }

  function syncDropdown(dropdown) {
    if (!dropdown || !dropdown.querySelectorAll) return;
    var today = isTodaySelected();
    var pastCutoff = today && isPastCutoff();
    var options = dropdown.querySelectorAll(OPTION_SELECTOR);

    options.forEach(function (option) {
      var label = (option.textContent || '').trim().toUpperCase();
      if (label === 'AM') {
        setOptionHidden(option, today); // hide AM whenever today is selected
      } else if (label === 'PM') {
        setOptionHidden(option, pastCutoff); // hide PM only once past cutoff, same-day
      }
    });
  }

  function scanAllDropdowns() {
    document.querySelectorAll(DROPDOWN_SELECTOR).forEach(syncDropdown);
  }

  // Safety net: clear an already-selected value that's no longer valid —
  // AM for today at any time, or PM for today once past cutoff — so it can
  // never be submitted with the order even if a dropdown was left open
  // mid-change or the cutoff passed while the tab was open.
  function enforceValidSelection() {
    if (!isTodaySelected()) return;
    var pastCutoff = isPastCutoff();

    document.querySelectorAll('input.deliverytimeValues, input.deliverytimeValue').forEach(function (input) {
      var value = (input.value || '').trim().toUpperCase();
      var invalid = value === 'AM' || (value === 'PM' && pastCutoff);
      if (!invalid) return;

      var setter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
      setter.call(input, '');
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  function runCheck() {
    scanAllDropdowns();
    enforceValidSelection();
  }

  let initialized = false;

  // Temporarily hide checkout button and delivery date calendar
  var style = document.createElement('style');
  style.textContent = '#delivery_date, button[name="checkout"] { display: none !important; }';
  document.head.appendChild(style);

  function init() {

    if (initialized || !document.body) {
      return;
    };

    initialized = true;

    // Restore checkout button and delivery date calendar
    style.remove();

    var observer = new MutationObserver(runCheck);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    // Catch the 1:00 PM boundary even if nothing else on the page changes.
    // setInterval(runCheck, RECHECK_INTERVAL_MS);
    runCheck();
  }

  // Initial pass in case the page loads with today already selected.
  document.addEventListener('DOMContentLoaded', function() {
    init();
  });

  init();
})();









/**
 * Cart-page "Water products minimum" inline warning row
 * --------------------------------------------------------
 * WHY THIS SCRIPT EXISTS:
 * This is a UI/UX-only cart-page notice. It does not enforce or block
 * anything — it does not touch the checkout button or checkout flow at all.
 * It renders a warning-styled row directly after the cart's line item list
 * (not a floating popup, and NOT inserted as a child inside the list — see
 * important note below), telling the customer that Water products won't be
 * delivered unless the Water subtotal reaches €50.
 *
 * RULE:
 * If the cart contains Water-collection products AND their combined
 * subtotal is less than €50, show the warning row:
 *   ⚠️ Water Delivery Requirement
 *   Water products are available for delivery only when your total water
 *   purchase is €50 or more. If your water total is below €50, pickup only
 *   will be available.
 *   €50+ of water: Delivery available with a €10 delivery charge.
 *   Less than €50 of water: Pickup only.
 * If there are no Water products in the cart, or the Water subtotal is
 * €50 or more, the row is removed/hidden. (Heading/body text is configurable
 * via CONFIG.heading / CONFIG.bodyLines below.)
 *
 * HOW WATER ITEMS ARE IDENTIFIED / DATA SOURCE:
 * Not read from the DOM for the calculation. Cart contents come from
 * Shopify's own cart AJAX endpoint (/cart.js), and Water-collection
 * membership comes from /collections/<handle>/products.json — both fetched
 * directly, independent of whatever is currently rendered on the page. The
 * DOM is only touched to render the warning row itself.
 *
 * IMPORTANT — WHY THIS IS A SIBLING, NOT A CHILD, OF THE CART ITEMS LIST:
 * An earlier version of this script appended the warning as the last CHILD
 * inside the cart items container. That broke the live theme: many themes
 * iterate over every child of that container assuming each one is a real
 * line-item row (with quantity inputs, remove buttons, etc.) and attach
 * event listeners to elements inside each child — e.g.
 * `row.querySelector('.qty-input').addEventListener(...)`. Our row has none
 * of that structure, so the theme's own script threw
 * "Uncaught TypeError: Cannot read properties of null (reading
 * 'addEventListener')" the moment it reached our row. This version instead
 * inserts the warning as a SIBLING placed immediately after the cart items
 * container (or after the whole <table> if the container is a <tbody>/​
 * <table>, since a <div> isn't valid inside table markup). Visually it still
 * lands directly below the cart items — but the theme's own per-item JS
 * never walks over it, since it isn't one of the container's children.
 *
 * THEME MARKUP:
 * Confirmed against the live Farsons Direct cart page — line items render as
 * `ul.cartitems--list > li.cart-item`, so `cartItemsContainerSelectors`
 * targets `ul.cartitems--list` (with a couple of generic fallbacks). The
 * alert is inserted right after that <ul>, so verify the visual placement
 * and check for console errors on the actual cart page before shipping —
 * ideally on the dev store first, since theme updates can still change this
 * markup later.
 *
 * Drop this into your theme (e.g. assets/cart-water-minimum-alert.js) and
 * load it on the cart page template only (cart.liquid / sections/main-cart.liquid),
 * e.g.: {{ 'cart-water-minimum-alert.js' | asset_url | script_tag }}
 */

(function () {
  const CONFIG = {
    // Collection to check the subtotal of
    waterCollectionHandle: 'water',
    // Minimum Water subtotal required, in the shop's currency's smallest unit (cents)
    minimumWaterCents: 5000, // €50.00
    // How often to re-check (ms) — covers quantity changes and DOM re-renders
    pollIntervalMs: 800,
    heading: 'Water Delivery Requirement',
    bodyLines: [
      'Water products are available for delivery only when your total water purchase is €50 or more. If your water total is below €50, pickup only will be available.',
      '€50+ of water: Delivery available with a €10 delivery charge.',
      'Less than €50 of water: Pickup only.',
    ],
    // Selectors for the element that directly contains the cart's line items
    // (one <li class="cart-item"> per line item). Confirmed against the live
    // Farsons Direct cart markup: items render as `ul.cartitems--list > li.cart-item`.
    // Kept as a list (checked in order) with a couple of generic fallbacks in
    // case the markup changes or this runs on a different template variant.
    cartItemsContainerSelectors: [
      'ul.cartitems--list',
      '.cart-items',
      '.cart__contents',
    ],
  };

  const ROW_ID = 'water-min-alert-row';
  let waterProductIds = null; // Set<number>, populated once from the collection

  // ---------- data ----------

  async function getWaterProductIds() {
    if (waterProductIds) return waterProductIds;
    try {
      const res = await fetch(`/collections/${CONFIG.waterCollectionHandle}/products.json?limit=250`);
      const data = await res.json();
      waterProductIds = new Set((data.products || []).map((p) => p.id));
    } catch (err) {
      console.error('Water minimum alert: failed to fetch Water collection products', err);
      waterProductIds = new Set();
    }
    return waterProductIds;
  }

  async function getWaterSubtotalCents() {
    const [cartRes, waterIds] = await Promise.all([fetch('/cart.js'), getWaterProductIds()]);
    const cart = await cartRes.json();

    let waterCents = 0;
    let waterItemCount = 0;
    for (const item of cart.items) {
      // item.product_id is the numeric product ID; matches IDs from the collection fetch
      if (waterIds.has(item.product_id)) {
        waterCents += item.line_price; // line_price is already qty * price, in cents
        waterItemCount += item.quantity;
      }
    }
    return { waterCents, waterItemCount };
  }

  // ---------- inline alert row ----------

  function findCartItemsContainer() {
    for (const selector of CONFIG.cartItemsContainerSelectors) {
      const el = document.querySelector(selector);
      if (el) return el;
    }
    return null;
  }

  const ALERT_STYLE = [
    'display:flex', 'flex-direction:column', 'gap:6px',
    'background:#fff3cd', 'color:#664d03', 'border:1px solid #ffe69c',
    'border-radius:6px', 'padding:12px 16px', 'margin:8px 0',
    'font-size:14px', 'width:100%', 'box-sizing:border-box',
  ].join(';');

  function buildAlertContent() {
    const wrap = document.createElement('div');
    wrap.style.cssText = ALERT_STYLE;
    wrap.setAttribute('role', 'alert');

    const heading = document.createElement('div');
    heading.style.cssText = 'display:flex;align-items:center;gap:8px;font-weight:700;';

    const icon = document.createElement('span');
    icon.textContent = '⚠️';
    icon.setAttribute('aria-hidden', 'true');
    icon.style.cssText = 'font-size:16px;line-height:1;flex-shrink:0;';
    heading.appendChild(icon);

    const headingText = document.createElement('strong');
    headingText.textContent = CONFIG.heading;
    heading.appendChild(headingText);

    wrap.appendChild(heading);

    CONFIG.bodyLines.forEach((line) => {
      const p = document.createElement('div');
      p.textContent = line;
      wrap.appendChild(p);
    });

    return wrap;
  }

  // NOTE: we deliberately do NOT insert the alert as a child of the cart
  // items container. Many themes iterate over every child of that container
  // assuming each one is a real line-item row (with quantity inputs, remove
  // buttons, etc.) and attach event listeners to elements inside each child.
  // An alert row with none of that structure breaks that loop — e.g.
  // `row.querySelector('.qty-input').addEventListener(...)` throws
  // "Cannot read properties of null (reading 'addEventListener')" the moment
  // the theme's script reaches our row and finds no matching element inside
  // it. Instead we insert the alert as a SIBLING immediately after the
  // outer cart-items element (after the whole <table> if the container is a
  // <tbody>, since a <div> can't legally sit inside <tbody>/<table> markup).
  // This still renders as the last thing on the page directly below the
  // cart items, without the theme's per-item logic ever seeing it.

  function getInsertionAnchor(container) {
    // If the container is a table body, anchor after the whole table instead —
    // a <div> isn't valid table markup and would still get walked by some
    // table-row-iteration logic if placed inside <tbody>/<table>.
    if (container.tagName === 'TBODY' || container.tagName === 'TABLE') {
      return container.closest('table') || container;
    }
    return container;
  }

  function showAlertRow() {
    const container = findCartItemsContainer();
    if (!container) {
      console.warn('Water minimum alert: no matching cart items container found — check cartItemsContainerSelectors in the config.');
      return;
    }

    const anchor = getInsertionAnchor(container);

    let row = document.getElementById(ROW_ID);
    if (row && row.previousElementSibling === anchor && row.parentElement === anchor.parentElement) {
      // Already present and correctly positioned — nothing to do.
      return;
    }
    if (row) row.remove(); // stale row from a previous render/container

    row = document.createElement('div');
    row.id = ROW_ID;
    row.appendChild(buildAlertContent());

    anchor.insertAdjacentElement('afterend', row); // renders directly after the cart items, as a sibling — not a child
  }

  function hideAlertRow() {
    const row = document.getElementById(ROW_ID);
    if (row) row.remove();
  }

  // ---------- main loop ----------

  async function checkAndRender() {
    const { waterCents, waterItemCount } = await getWaterSubtotalCents();

    // No Water products in the cart at all — nothing to warn about.
    if (waterItemCount === 0) {
      hideAlertRow();
      return;
    }

    if (waterCents < CONFIG.minimumWaterCents) {
      showAlertRow();
    } else {
      hideAlertRow();
    }
  }

  // Initial check on page load
  document.addEventListener('DOMContentLoaded', checkAndRender);

  // Re-check periodically to catch quantity changes and cart DOM re-renders
  // (theme-dependent: many themes also fire a custom event like 'cart:updated' —
  // hook into that instead of/in addition to polling if your theme provides one, e.g.:
  // document.addEventListener('cart:updated', checkAndRender);
  // Polling also re-appends the row on every tick if the theme's AJAX cart
  // re-render wiped it out, keeping it pinned as the last item.
  setInterval(checkAndRender, CONFIG.pollIntervalMs);
})();







// document.addEventListener("DOMContentLoaded", function() {
//   function checkSameDayAvailability(calendar) {
//     const now = new Date();
//     const today = now.getDate();
//     const currentMonth = now.toLocaleString("default", { month: "long" }); // e.g. September
//     const currentYear = now.getFullYear().toString(); // e.g. 2025

//     const header = calendar.querySelector(".rmdp-header-values");
//     if (!header) return;

//     const headerMonth = header.querySelector("span:nth-child(1)")?.innerText.replace(",", "").trim();
//     const headerYear = header.querySelector("span:nth-child(2)")?.innerText.trim();

//     if (headerMonth !== currentMonth || headerYear !== currentYear) {
//       return; // not showing current month
//     }

//     // Find today's cell
//     const days = calendar.querySelectorAll(".rmdp-day");
//     let todayCell = null;
//     days.forEach(day => {
//       const span = day.querySelector("span");
//       if (span && parseInt(span.innerText) === today) {
//         todayCell = day;
//       }
//     });

//     if (todayCell && todayCell.classList.contains("rmdp-disabled")) {
//       // showSameDayPopup();
//     } 
//   }

//   function showSameDayPopup() {
//     // DISABLE THIS POPUP
//     return;
    
//     // Prevent duplicate popups
//     // if (document.getElementById("sameDayPopup")) return;

//     // const message = "We’re really sorry — same-day delivery isn’t available right now due to high demand. Our team is flat out making sure every order gets out quickly, and yours will be no exception. Please pick the next best date at checkout.";

//     // const modal = document.createElement("div");
//     // modal.id = "sameDayPopup";
//     // modal.style.position = "fixed";
//     // modal.style.top = "0";
//     // modal.style.left = "0";
//     // modal.style.width = "100%";
//     // modal.style.height = "100%";
//     // modal.style.backgroundColor = "rgba(0,0,0,0.6)";
//     // modal.style.display = "flex";
//     // modal.style.alignItems = "center";
//     // modal.style.justifyContent = "center";
//     // modal.style.zIndex = "9999";

//     // const box = document.createElement("div");
//     // box.style.background = "#fff";
//     // box.style.padding = "20px";
//     // box.style.maxWidth = "500px";
//     // box.style.borderRadius = "8px";
//     // box.style.textAlign = "center";
//     // box.innerHTML = `
//     //   <p style="font-size:16px; margin-bottom:15px;">${message}</p>
//     //   <button id="closeSameDayPopup" style="background:#007fff; color:#fff; border:none; padding:10px 20px; border-radius:4px; cursor:pointer;">
//     //     OK
//     //   </button>
//     // `;

//     // modal.appendChild(box);
//     // document.body.appendChild(modal);

//     // document.getElementById("closeSameDayPopup").addEventListener("click", function() {
//     //   modal.remove();
//     // });
//   }

//   // Watch for the calendar rendering
//   const observer = new MutationObserver(() => {
//     const calendar = document.querySelector(".rmdp-calendar");
//     if (calendar) {
//       checkSameDayAvailability(calendar);
//       observer.disconnect(); // stop watching once calendar is found + checked
//     }
//   });

//   observer.observe(document.body, { childList: true, subtree: true });
// });